import { useEffect, useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Eraser, PenLine } from 'lucide-react';
import { Button } from '../ui/Button';
// Side-effect import: patches signature_pad v2's crash on multi-touch and on a
// clear() that lands mid-stroke. Must be imported before any pad is built.
import '../../lib/signaturePad';

/**
 * Modal containing a signature canvas. The inductee signs on the
 * tablet; on Confirm we hand the parent a base64 PNG (data URL form,
 * which the server's decodeSignaturePng accepts).
 *
 * Esc closes; Cancel closes without signing; Clear empties the pad.
 *
 * Sizing: the underlying signature_pad library doesn't auto-resize.
 * If the canvas's CSS size differs from its internal resolution
 * (the `width`/`height` attributes), pointer coordinates get
 * progressively more wrong the further right you draw. Below we
 * size the canvas to its rendered box × devicePixelRatio on mount
 * and on every container resize, then scale the 2D context to
 * compensate. That keeps the pen tip glued to the finger / cursor.
 */
export function SignaturePadModal({
  open,
  title,
  onClose,
  onConfirm,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  onConfirm: (dataUrl: string) => void | Promise<void>;
}) {
  const padRef = useRef<SignatureCanvas | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [empty, setEmpty] = useState(true);

  // Esc to close while open (but never mid-submit: the KitIssued POST
  // is already in flight and closing would discard the operator's
  // context while the write may still land). Mounted only when open.
  useEffect(() => {
    if (!open) return;
    function handler(e: KeyboardEvent) {
      if (e.key === 'Escape' && !submitting) onClose();
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose, submitting]);

  // Keep the canvas's internal resolution in sync with its rendered
  // size. Runs once on open and on every container resize.
  useEffect(() => {
    if (!open) return;
    const initialPad = padRef.current;
    if (!initialPad) return;
    const canvas = initialPad.getCanvas();

    function resize() {
      const pad = padRef.current;
      if (!pad) return;
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      const cssWidth = canvas.offsetWidth;
      const cssHeight = canvas.offsetHeight;
      if (cssWidth === 0 || cssHeight === 0) return;
      canvas.width = cssWidth * ratio;
      canvas.height = cssHeight * ratio;
      const ctx = canvas.getContext('2d');
      ctx?.scale(ratio, ratio);
      // Clearing avoids smearing the existing stroke at a new scale.
      pad.clear();
      setEmpty(true);
    }

    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    window.addEventListener('orientationchange', resize);
    return () => {
      ro.disconnect();
      window.removeEventListener('orientationchange', resize);
    };
  }, [open]);

  if (!open) return null;

  function clear() {
    padRef.current?.clear();
    setEmpty(true);
  }

  async function confirm() {
    const pad = padRef.current;
    if (!pad || pad.isEmpty()) return;
    const dataUrl = pad.getTrimmedCanvas().toDataURL('image/png');
    setSubmitting(true);
    try {
      await onConfirm(dataUrl);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="signature-title"
      // z-[80]: the pad is always the final interaction and must sit above
      // whatever launched it; both the issue drawer (z-50) and the handover
      // checklist modal (z-[70]) open it.
      className="fixed inset-0 z-[80] flex items-center justify-center bg-fg/40 p-4 backdrop-blur-sm aurora-fade-in"
    >
      <div className="w-full max-w-2xl overflow-hidden rounded-token-lg border border-line/10 bg-surface shadow-token-lg">
        <header className="border-b border-divider/10 px-6 py-4">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-accent">
            <PenLine className="h-3.5 w-3.5" />
            <span>Signature</span>
          </div>
          <h2
            id="signature-title"
            className="mt-1 text-lg font-semibold tracking-tight text-fg"
          >
            {title}
          </h2>
          <p className="mt-1 text-sm text-fg-muted">
            Hand the device to the inductee. Sign in the box below to
            confirm receipt.
          </p>
        </header>

        <div className="bg-surface-inset p-4 sm:p-6">
          {/* The signature canvas itself stays white-with-charcoal-ink
              irrespective of theme: literal white, not bg-surface (which
              goes dark in dark mode and hid the charcoal ink while
              signing). The stored PNG keeps dark ink for the PDFs. */}
          <div className="overflow-hidden rounded-token-sm border-2 border-dashed border-line-strong/30 bg-white">
            <SignatureCanvas
              ref={padRef}
              penColor="#0f172a"
              onBegin={() => setEmpty(false)}
              canvasProps={{
                className: 'block h-56 w-full touch-none',
              }}
            />
          </div>
          <p className="mt-2 text-center text-xs text-fg-muted">
            Sign above. Tap clear to start over.
          </p>
        </div>

        <footer className="flex items-center justify-between gap-3 border-t border-divider/10 bg-surface-2 px-4 py-3 sm:px-6">
          <Button
            variant="ghost"
            size="md"
            iconLeft={<Eraser className="h-4 w-4" />}
            onClick={clear}
            disabled={empty || submitting}
          >
            Clear
          </Button>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="md"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="md"
              onClick={confirm}
              disabled={empty}
              loading={submitting}
            >
              Confirm and save
            </Button>
          </div>
        </footer>
      </div>
    </div>
  );
}
